import React, { useEffect, useState, useRef } from "react";
import { getQuestionsAndOptions, saveAnswer, checkExamAvailability } from "../../../api";
import { useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import {
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  Bars3Icon,
  XMarkIcon,
  FlagIcon,
  TrashIcon,
  PaperAirplaneIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { FlagIcon as FlagIconSolid } from '@heroicons/react/24/solid';

function Exam() {
  const { eid } = useParams();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionStatus, setQuestionStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(1800);
  const [showSidebar, setShowSidebar] = useState(false);
  const timerId = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user) {
      navigate("/login");
      return;
    }

    const examSubmitted = sessionStorage.getItem("exam-submitted");
    if (examSubmitted) {
      navigate("/student/exam");
      return;
    }

    const initializeExam = async () => {
      try {
        console.log('🔍 Checking exam availability for ID:', eid);
        const availabilityResult = await checkExamAvailability(eid);
        console.log('📊 Availability result:', availabilityResult);

        if (!availabilityResult.available) {
          Swal.fire({
            icon: 'error',
            title: 'Exam Not Available',
            text: availabilityResult.message,
            confirmButtonText: 'Back to Exams',
            confirmButtonColor: '#3b82f6'
          }).then(() => {
            navigate('/student/exam');
          });
          return;
        }

        console.log('🔍 Fetching questions for exam ID:', eid);
        const data = await getQuestionsAndOptions(eid);

        console.log('📥 Raw API response:', data);

        if (data && data.error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: data.error,
            confirmButtonColor: '#ef4444'
          });
          setTimeout(() => navigate('/student/exam'), 2000);
          return;
        }

        if (!Array.isArray(data) || data.length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'No Questions',
            text: 'No questions available for this exam!',
            confirmButtonColor: '#f59e0b'
          });
          setTimeout(() => navigate('/student/exam'), 2000);
          return;
        }

        console.log('🔍 Validating questions...');
        let questionsWithoutOptions = 0;

        data.forEach((q, idx) => {
          const hasOptions = Array.isArray(q.options) && q.options.length > 0;
          if (!hasOptions) {
            questionsWithoutOptions++;
            console.error(`❌ Question ${q.qid} has NO OPTIONS!`);
          }
        });

        if (questionsWithoutOptions > 0) {
          console.warn(`⚠️ Warning: ${questionsWithoutOptions} questions are missing options!`);
        }

        setQuestions(data);

        const initialStatus = data.reduce((acc, question) => {
          acc[question.qid] = "Not Visited";
          return acc;
        }, {});
        setQuestionStatus(initialStatus);
        setLoading(false);
      } catch (error) {
        console.error("❌ Error initializing exam:", error);
        Swal.fire({
          icon: 'error',
          title: 'Failed to Load',
          text: 'Failed to load exam: ' + error.message,
          confirmButtonColor: '#ef4444'
        });
        setTimeout(() => navigate('/student/exam'), 2000);
      }
    };

    initializeExam();

    const savedTimeLeft = localStorage.getItem("exam-timer");
    if (savedTimeLeft) {
      setTimeLeft(parseInt(savedTimeLeft));
    }

    startTimer();

    return () => {
      if (timerId.current) {
        clearInterval(timerId.current);
      }
    };
  }, [eid, navigate]);

  const startTimer = () => {
    if (timerId.current) {
      clearInterval(timerId.current);
    }

    timerId.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerId.current);
          handleSubmit();
          return 0;
        }
        localStorage.setItem("exam-timer", prevTime - 1);
        return prevTime - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const updateQuestionStatus = (qid, status) => {
    setQuestionStatus((prevStatus) => ({
      ...prevStatus,
      [qid]: status
    }));
  };

  const handleNext = () => {
    const currentQid = questions[currentQuestionIndex].qid;
    if (questionStatus[currentQid] === "Not Visited" && !answers[currentQid]) {
      updateQuestionStatus(currentQid, "Not Answered");
    }

    setCurrentQuestionIndex((prevIndex) => {
      const newIndex = Math.min(prevIndex + 1, questions.length - 1);
      const nextQid = questions[newIndex].qid;
      if (questionStatus[nextQid] === "Not Visited") {
        updateQuestionStatus(nextQid, "Not Answered");
      }
      return newIndex;
    });
  };

  const handlePrevious = () => {
    setCurrentQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const handleAnswerChange = (optionId) => {
    const currentQid = questions[currentQuestionIndex].qid;
    const currentStatus = questionStatus[currentQid];
    
    console.log(`✅ Selected option ID: ${optionId} for question: ${currentQid}`);
    setAnswers(prev => ({ ...prev, [currentQid]: optionId }));
    
    if (currentStatus === "Marked for Review") {
      updateQuestionStatus(currentQid, "Answered & Marked for Review");
    } else {
      updateQuestionStatus(currentQid, "Answered");
    }
  };

  const handleMarkForReview = () => {
    const currentQid = questions[currentQuestionIndex].qid;
    const currentStatus = questionStatus[currentQid];
    
    if (currentStatus === "Answered") {
      updateQuestionStatus(currentQid, "Answered & Marked for Review");
    } else if (currentStatus === "Answered & Marked for Review") {
      updateQuestionStatus(currentQid, "Answered");
    } else if (currentStatus === "Marked for Review") {
      updateQuestionStatus(currentQid, "Not Answered");
    } else {
      updateQuestionStatus(currentQid, "Marked for Review");
    }
  };

  const handleClearAnswer = () => {
    const currentQid = questions[currentQuestionIndex].qid;
    const currentStatus = questionStatus[currentQid];
    
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[currentQid];
      return newAnswers;
    });
    
    if (currentStatus === "Answered & Marked for Review") {
      updateQuestionStatus(currentQid, "Marked for Review");
    } else {
      updateQuestionStatus(currentQid, "Not Answered");
    }
  };

  const handleSubmit = async () => {
    if (timerId.current) {
      clearInterval(timerId.current);
    }

    const answeredCount = Object.keys(answers).length;
    const totalQuestions = questions.length;

    const result = await Swal.fire({
      title: 'Submit Exam?',
      html: `
        <div class="text-left space-y-3 p-2">
          <div class="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
            <p class="text-gray-800 font-medium">Questions Answered:</p>
            <p class="text-blue-600 text-2xl font-bold">${answeredCount} / ${totalQuestions}</p>
          </div>
          <div class="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
            <p class="text-amber-800 text-sm font-semibold flex items-center">
              <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
              Once submitted, you cannot change your answers
            </p>
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: '✓ Submit Now',
      cancelButtonText: '← Review Answers',
      customClass: {
        popup: 'rounded-2xl shadow-2xl',
        title: 'text-2xl font-bold',
        htmlContainer: 'text-base'
      }
    });

    if (!result.isConfirmed) {
      startTimer();
      return;
    }

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const answersArray = questions.map((question) => ({
        question_id: parseInt(question.qid),
        selected_option: answers[question.qid] || 0,
        marked_for_review: questionStatus[question.qid] === "Answered & Marked for Review" ||
          questionStatus[question.qid] === "Marked for Review"
      }));

      const answersData = {
        eid: parseInt(eid),
        uid: user.uid,
        answers: answersArray
      };

      console.log('📤 Submitting answers:', answersData);
      const response = await saveAnswer(answersData);
      console.log('✅ Server response:', response);

      if (response.success) {
        localStorage.removeItem("exam-timer");
        sessionStorage.setItem("exam-submitted", "true");

        await Swal.fire({
          icon: 'success',
          title: '🎉 Exam Submitted!',
          html: `
            <div class="text-center space-y-4 py-4">
              <div class="bg-green-50 border-2 border-green-200 rounded-xl p-5">
                <p class="text-gray-700 text-lg mb-3">Your exam has been submitted successfully!</p>
                <div class="bg-white rounded-lg p-4 shadow-sm">
                  <p class="text-green-600 font-bold text-3xl">${answeredCount}</p>
                  <p class="text-gray-600 text-sm">out of ${totalQuestions} questions answered</p>
                </div>
              </div>
              <div class="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
                <p class="text-blue-900 font-bold text-xl mb-2">📊 Results Coming Soon</p>
                <p class="text-gray-600">Your results will be announced later. Please check back soon!</p>
              </div>
            </div>
          `,
          confirmButtonText: 'Go to Dashboard',
          confirmButtonColor: '#3b82f6',
          allowOutsideClick: false,
          customClass: {
            popup: 'rounded-2xl shadow-2xl',
            confirmButton: 'rounded-xl px-8 py-3 text-lg font-semibold'
          }
        });

        navigate("/student/exam");
      } else {
        throw new Error(response.error || 'Submission failed');
      }
    } catch (error) {
      console.error("❌ Error submitting answers:", error);
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: error.message || 'Failed to submit exam. Please try again.',
        confirmButtonText: 'Retry',
        confirmButtonColor: '#ef4444'
      });
      startTimer();
    }
  };

  const jumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setShowSidebar(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="relative mb-8">
          <div className="w-24 h-24 border-8 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <CheckCircleIcon className="w-10 h-10 text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Your Exam</h2>
        <p className="text-gray-600 animate-pulse">Preparing questions...</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
        <div className="bg-white p-10 rounded-2xl shadow-2xl text-center max-w-md border-t-4 border-red-500">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">No Questions Found</h2>
          <p className="text-gray-600 mb-6">This exam doesn't have any questions available right now.</p>
          <button
            onClick={() => navigate('/student/exam')}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold shadow-lg transition-all"
          >
            Back to Exams
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  if (!currentQuestion) {
    return <div className="min-h-screen flex items-center justify-center text-red-600 font-semibold">Error loading question</div>;
  }

  const questionOptions = Array.isArray(currentQuestion.options) ? currentQuestion.options : [];
  const answeredCount = Object.values(questionStatus).filter(s => s === 'Answered' || s === 'Answered & Marked for Review').length;
  const progressPercentage = Math.round((answeredCount / questions.length) * 100);
  
  const currentQid = currentQuestion.qid;
  const currentStatus = questionStatus[currentQid];
  const isMarkedForReview = currentStatus === "Marked for Review" || currentStatus === "Answered & Marked for Review";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* ✅ FIXED: Top Sticky Bar */}
      <div className="bg-white border-b-2 border-gray-200 sticky top-0 z-[60] shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            
            {/* Left: Question Counter + Sidebar Toggle */}
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowSidebar(!showSidebar)}
                className="lg:hidden p-2.5 rounded-xl bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors shadow-sm"
              >
                {showSidebar ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
              </button>
              <div className="flex items-center space-x-2">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-sm px-4 py-2 rounded-xl shadow-lg">
                  Q {currentQuestionIndex + 1}
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-xs text-gray-500 leading-tight">Question</p>
                  <p className="text-sm font-bold text-gray-800 leading-tight">{currentQuestionIndex + 1}/{questions.length}</p>
                </div>
              </div>
            </div>

            {/* Center: Progress Bar (Desktop) */}
            <div className="hidden md:flex items-center space-x-3 flex-1 max-w-md">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-600">Progress</span>
                  <span className="text-xs font-bold text-blue-600">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                  <div 
                    className="bg-gradient-to-r from-green-500 via-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 shadow-lg"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">{answeredCount} answered</span>
                  <span className="text-xs text-gray-500">{questions.length - answeredCount} remaining</span>
                </div>
              </div>
            </div>

            {/* Right: Timer */}
            <div className={`flex items-center space-x-2 px-3 py-2 rounded-xl shadow-lg ${
              timeLeft < 300 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-blue-100 text-blue-600'
            }`}>
              <ClockIcon className="w-5 h-5" />
              <div className="text-right">
                <p className="text-xs font-medium opacity-80 leading-tight hidden sm:block">Time</p>
                <p className="text-lg sm:text-xl font-bold leading-tight">{formatTime(timeLeft)}</p>
              </div>
            </div>
          </div>

          {/* Mobile Progress Bar */}
          <div className="md:hidden mt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-600">Progress: {progressPercentage}%</span>
              <span className="text-xs font-semibold text-blue-600">{answeredCount}/{questions.length}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 shadow-inner">
              <div 
                className="bg-gradient-to-r from-green-500 via-blue-500 to-indigo-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 lg:py-6">
        <div className="flex gap-6">
          
          {/* Main Question Card */}
          <div className="flex-1 min-w-0">
            <div className={`bg-white rounded-2xl shadow-xl border-2 overflow-hidden transition-all ${
              isMarkedForReview ? 'border-orange-400 ring-4 ring-orange-100' : 'border-gray-200'
            }`}>
              
              {/* Flag Banner */}
              {isMarkedForReview && (
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 flex items-center space-x-2 text-sm font-semibold">
                  <FlagIconSolid className="w-4 h-4" />
                  <span>Marked for review</span>
                </div>
              )}
              
              {/* Question Header */}
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 lg:p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-2">
                    <span className="bg-white text-blue-600 font-black text-sm px-4 py-1.5 rounded-xl shadow-lg">
                      #{currentQuestionIndex + 1}
                    </span>
                    <span className="bg-white bg-opacity-20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-xl border border-white border-opacity-30">
                      {currentQuestion.group_title || 'General'}
                    </span>
                  </div>
                  <span className="bg-white text-green-600 font-black text-xs px-4 py-1.5 rounded-xl shadow-lg flex items-center space-x-1">
                    <span>{currentQuestion.mark}</span>
                    <span className="text-xs">marks</span>
                  </span>
                </div>
              </div>

              {/* Question Body */}
              <div className="p-4 lg:p-8">
                {/* Question Text */}
                <div className="prose max-w-none mb-6 lg:mb-8">
                  <div className="text-base lg:text-lg text-gray-800 leading-relaxed font-medium" dangerouslySetInnerHTML={{ __html: currentQuestion.question }} />
                </div>

                {/* Options */}
                {questionOptions.length > 0 ? (
                  <div className="space-y-3 lg:space-y-4">
                    {questionOptions.map((option, idx) => {
                      const isSelected = answers[currentQuestion.qid] === option.oid;
                      const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
                      return (
                        <label
                          key={option.oid}
                          className={`group flex items-start p-4 lg:p-5 rounded-xl lg:rounded-2xl cursor-pointer transition-all duration-200 border-2 ${
                            isSelected
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-500 shadow-lg scale-[1.01]'
                              : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md hover:bg-blue-50'
                          }`}
                        >
                          <div className={`flex-shrink-0 w-9 h-9 lg:w-11 lg:h-11 rounded-full flex items-center justify-center font-bold text-sm lg:text-base mr-3 lg:mr-4 transition-all ${
                            isSelected 
                              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white scale-110 shadow-lg' 
                              : 'bg-gray-100 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600'
                          }`}>
                            {letters[idx]}
                          </div>
                          <input
                            type="radio"
                            name={`question-${currentQuestion.qid}`}
                            value={option.oid}
                            checked={isSelected}
                            onChange={() => handleAnswerChange(option.oid)}
                            className="hidden"
                          />
                          <div className="flex-1 text-gray-700 text-sm lg:text-base pt-1 lg:pt-1.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: option.name }} />
                          {isSelected && (
                            <div className="flex-shrink-0 ml-3 lg:ml-4">
                              <div className="w-7 h-7 lg:w-8 lg:h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                                <CheckIcon className="w-4 h-4 lg:w-5 lg:h-5 text-white font-bold" />
                              </div>
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 lg:p-6 rounded-xl flex items-start space-x-3 lg:space-x-4">
                    <ExclamationTriangleIcon className="w-6 h-6 lg:w-8 lg:h-8 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="text-red-800 font-bold text-base lg:text-lg">No Options Available</p>
                      <p className="text-red-600 text-xs lg:text-sm mt-1">Question ID: {currentQuestion.qid}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="border-t-2 border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50 p-4 lg:p-5">
                <div className="flex items-center justify-between gap-2 lg:gap-3 flex-wrap">
                  
                  {/* Previous Button */}
                  <button
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                    className="flex items-center space-x-1 lg:space-x-2 px-4 lg:px-6 py-2.5 lg:py-3 bg-gray-600 text-white rounded-xl font-semibold hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl disabled:shadow-none text-sm lg:text-base"
                  >
                    <ChevronLeftIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                    <span className="hidden sm:inline">Previous</span>
                  </button>

                  {/* Middle Actions */}
                  <div className="flex gap-2 lg:gap-3">
                    <button
                      onClick={handleClearAnswer}
                      className="flex items-center space-x-1 lg:space-x-2 px-4 lg:px-5 py-2.5 lg:py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-xl text-sm lg:text-base"
                      title="Clear Answer"
                    >
                      <TrashIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                      <span className="hidden sm:inline">Clear</span>
                    </button>
                    <button
                      onClick={handleMarkForReview}
                      className={`flex items-center space-x-1 lg:space-x-2 px-4 lg:px-5 py-2.5 lg:py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl text-sm lg:text-base ${
                        isMarkedForReview 
                          ? 'bg-orange-600 text-white hover:bg-orange-700' 
                          : 'bg-orange-500 text-white hover:bg-orange-600'
                      }`}
                      title={isMarkedForReview ? "Remove Flag" : "Mark for Review"}
                    >
                      {isMarkedForReview ? <FlagIconSolid className="w-4 h-4 lg:w-5 lg:h-5" /> : <FlagIcon className="w-4 h-4 lg:w-5 lg:h-5" />}
                      <span className="hidden sm:inline">{isMarkedForReview ? 'Flagged' : 'Flag'}</span>
                    </button>
                  </div>

                  {/* Next/Submit Button */}
                  {currentQuestionIndex < questions.length - 1 ? (
                    <button
                      onClick={handleNext}
                      className="flex items-center space-x-1 lg:space-x-2 px-4 lg:px-6 py-2.5 lg:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl text-sm lg:text-base"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRightIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      className="flex items-center space-x-1 lg:space-x-2 px-5 lg:px-8 py-2.5 lg:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all shadow-xl hover:shadow-2xl text-sm lg:text-base"
                    >
                      <PaperAirplaneIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                      <span>Submit</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ✅ FIXED: Sidebar - Question Palette (Mobile Optimized) */}
          {showSidebar && (
            <div className="fixed inset-0 z-[70] lg:hidden">
              {/* Backdrop */}
              <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={() => setShowSidebar(false)}></div>
              
              {/* Sidebar Panel */}
              <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl overflow-y-auto">
                
                {/* Header */}
                <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 p-4 sticky top-0 z-10">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold text-base">Question Palette</h3>
                    <button onClick={() => setShowSidebar(false)} className="text-white hover:bg-white hover:bg-opacity-20 p-1.5 rounded-lg transition-colors">
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="bg-white bg-opacity-25 backdrop-blur-md rounded-xl px-3 py-2 border border-white border-opacity-30">
                    <p className="text-white text-xs opacity-90">Answered Questions</p>
                    <p className="text-white font-black text-2xl">{answeredCount}<span className="text-lg opacity-80">/{questions.length}</span></p>
                    <div className="mt-2 bg-white bg-opacity-20 rounded-full h-1.5">
                      <div 
                        className="bg-white h-1.5 rounded-full transition-all"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Question Grid */}
                <div className="p-4">
                  <div className="grid grid-cols-5 gap-2">
                    {questions.map((q, index) => {
                      const status = questionStatus[q.qid];
                      let colorClass = 'bg-gray-200 text-gray-700 active:bg-gray-300';
                      
                      if (status === 'Answered') {
                        colorClass = 'bg-gradient-to-br from-green-500 to-emerald-600 text-white active:from-green-600';
                      } else if (status === 'Answered & Marked for Review') {
                        colorClass = 'bg-gradient-to-br from-purple-500 to-purple-600 text-white active:from-purple-600';
                      } else if (status === 'Not Answered') {
                        colorClass = 'bg-gradient-to-br from-red-500 to-pink-600 text-white active:from-red-600';
                      } else if (status === 'Marked for Review') {
                        colorClass = 'bg-gradient-to-br from-orange-500 to-amber-600 text-white active:from-orange-600';
                      }

                      return (
                        <button
                          key={q.qid}
                          onClick={() => jumpToQuestion(index)}
                          className={`${colorClass} ${
                            currentQuestionIndex === index ? 'ring-4 ring-blue-500 ring-offset-2 scale-110 shadow-xl' : 'shadow-md'
                          } aspect-square rounded-xl font-bold text-sm transition-all flex items-center justify-center active:scale-95 relative`}
                        >
                          {index + 1}
                          {(status === 'Marked for Review' || status === 'Answered & Marked for Review') && (
                            <FlagIconSolid className="w-2.5 h-2.5 absolute top-0.5 right-0.5" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Legend */}
                <div className="border-t-2 border-gray-200 p-4 space-y-2.5 text-xs bg-gray-50">
                  <div className="flex items-center justify-between py-0.5">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-md shadow"></div>
                      <span className="text-gray-700 font-medium">Answered</span>
                    </div>
                    <span className="font-bold text-gray-800 bg-green-100 px-2 py-0.5 rounded-full">{Object.values(questionStatus).filter(s => s === 'Answered').length}</span>
                  </div>
                  <div className="flex items-center justify-between py-0.5">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow"></div>
                      <span className="text-gray-700 font-medium">Answered & Flagged</span>
                    </div>
                    <span className="font-bold text-gray-800 bg-purple-100 px-2 py-0.5 rounded-full">{Object.values(questionStatus).filter(s => s === 'Answered & Marked for Review').length}</span>
                  </div>
                  <div className="flex items-center justify-between py-0.5">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-gradient-to-br from-red-500 to-pink-600 rounded-md shadow"></div>
                      <span className="text-gray-700 font-medium">Not Answered</span>
                    </div>
                    <span className="font-bold text-gray-800 bg-red-100 px-2 py-0.5 rounded-full">{Object.values(questionStatus).filter(s => s === 'Not Answered').length}</span>
                  </div>
                  <div className="flex items-center justify-between py-0.5">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-gradient-to-br from-orange-500 to-amber-600 rounded-md shadow"></div>
                      <span className="text-gray-700 font-medium">Flagged Only</span>
                    </div>
                    <span className="font-bold text-gray-800 bg-orange-100 px-2 py-0.5 rounded-full">{Object.values(questionStatus).filter(s => s === 'Marked for Review').length}</span>
                  </div>
                  <div className="flex items-center justify-between py-0.5">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-gray-200 rounded-md shadow"></div>
                      <span className="text-gray-700 font-medium">Not Visited</span>
                    </div>
                    <span className="font-bold text-gray-800 bg-gray-200 px-2 py-0.5 rounded-full">{Object.values(questionStatus).filter(s => s === 'Not Visited').length}</span>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="p-4 pt-0 bg-gray-50">
                  <button
                    onClick={handleSubmit}
                    className="w-full px-6 py-3.5 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white rounded-xl font-bold text-base hover:from-green-700 hover:via-emerald-700 hover:to-teal-700 shadow-2xl transition-all flex items-center justify-center space-x-2 active:scale-95"
                  >
                    <PaperAirplaneIcon className="w-5 h-5" />
                    <span>Submit Exam</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Sidebar */}
          <div className="hidden lg:block lg:w-80 lg:flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl shadow-2xl border-2 border-gray-200 overflow-hidden">
              
              {/* Sidebar Header */}
              <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-bold text-lg">Question Palette</h3>
                </div>
                <div className="bg-white bg-opacity-25 backdrop-blur-md rounded-xl px-4 py-3 border border-white border-opacity-30">
                  <p className="text-white text-xs opacity-90 mb-1">Answered Questions</p>
                  <p className="text-white font-black text-3xl">{answeredCount}<span className="text-xl opacity-80">/{questions.length}</span></p>
                  <div className="mt-2 bg-white bg-opacity-20 rounded-full h-2">
                    <div 
                      className="bg-white h-2 rounded-full transition-all"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Question Grid */}
              <div className="p-5 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <div className="grid grid-cols-5 gap-2.5">
                  {questions.map((q, index) => {
                    const status = questionStatus[q.qid];
                    let colorClass = 'bg-gray-200 text-gray-700 hover:bg-gray-300';
                    
                    if (status === 'Answered') {
                      colorClass = 'bg-gradient-to-br from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700';
                    } else if (status === 'Answered & Marked for Review') {
                      colorClass = 'bg-gradient-to-br from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700';
                    } else if (status === 'Not Answered') {
                      colorClass = 'bg-gradient-to-br from-red-500 to-pink-600 text-white hover:from-red-600 hover:to-pink-700';
                    } else if (status === 'Marked for Review') {
                      colorClass = 'bg-gradient-to-br from-orange-500 to-amber-600 text-white hover:from-orange-600 hover:to-amber-700';
                    }

                    return (
                      <button
                        key={q.qid}
                        onClick={() => jumpToQuestion(index)}
                        className={`${colorClass} ${
                          currentQuestionIndex === index ? 'ring-4 ring-blue-500 ring-offset-2 scale-110 shadow-xl' : 'shadow-md'
                        } aspect-square rounded-xl font-bold text-base transition-all flex items-center justify-center hover:scale-105 relative`}
                      >
                        {index + 1}
                        {(status === 'Marked for Review' || status === 'Answered & Marked for Review') && (
                          <FlagIconSolid className="w-3 h-3 absolute top-0.5 right-0.5" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="border-t-2 border-gray-200 p-5 space-y-3 text-sm bg-gray-50">
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-md shadow"></div>
                    <span className="text-gray-700 font-medium">Answered</span>
                  </div>
                  <span className="font-bold text-gray-800 bg-green-100 px-3 py-1 rounded-full">{Object.values(questionStatus).filter(s => s === 'Answered').length}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow"></div>
                    <span className="text-gray-700 font-medium">Answered & Flagged</span>
                  </div>
                  <span className="font-bold text-gray-800 bg-purple-100 px-3 py-1 rounded-full">{Object.values(questionStatus).filter(s => s === 'Answered & Marked for Review').length}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-gradient-to-br from-red-500 to-pink-600 rounded-md shadow"></div>
                    <span className="text-gray-700 font-medium">Not Answered</span>
                  </div>
                  <span className="font-bold text-gray-800 bg-red-100 px-3 py-1 rounded-full">{Object.values(questionStatus).filter(s => s === 'Not Answered').length}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-gradient-to-br from-orange-500 to-amber-600 rounded-md shadow"></div>
                    <span className="text-gray-700 font-medium">Flagged Only</span>
                  </div>
                  <span className="font-bold text-gray-800 bg-orange-100 px-3 py-1 rounded-full">{Object.values(questionStatus).filter(s => s === 'Marked for Review').length}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-gray-200 rounded-md shadow"></div>
                    <span className="text-gray-700 font-medium">Not Visited</span>
                  </div>
                  <span className="font-bold text-gray-800 bg-gray-200 px-3 py-1 rounded-full">{Object.values(questionStatus).filter(s => s === 'Not Visited').length}</span>
                </div>
              </div>

              {/* Submit Button */}
              <div className="p-5 pt-0 bg-gray-50">
                <button
                  onClick={handleSubmit}
                  className="w-full px-6 py-4 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white rounded-2xl font-bold text-lg hover:from-green-700 hover:via-emerald-700 hover:to-teal-700 shadow-2xl transition-all flex items-center justify-center space-x-3 hover:scale-105"
                >
                  <PaperAirplaneIcon className="w-6 h-6" />
                  <span>Submit Exam</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Exam;
